import { Activity } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useDispatchBoard, type DispatchActivity } from '../../lib/dispatch-board.js';

/**
 * Activity feed beneath the dispatch board.
 *
 * Two layers, newest first:
 *   1. Local zustand entries — this dispatcher's own actions, pushed
 *      optimistically at click time (instant feedback, page-local).
 *   2. Server ActivityEvent rows via GET /api/v1/activity — everything
 *      every device logged (assigns, completions, parts, photos,
 *      signatures, quote promotions). Survives refresh; 30s poll.
 *
 * Server rows that duplicate a local entry are fine — the local layer is
 * capped at 20 and labelled "this session"; the persisted list is the
 * record.
 */
export function ActivityFeed() {
  const localItems = useDispatchBoard((s) => s.activity);

  const serverQuery = useQuery({
    queryKey: ['activity', 'recent'],
    queryFn: ({ signal }) => api.activity.recent(50, signal),
    staleTime: 25_000,
    refetchInterval: 30_000,
  });
  const serverItems = serverQuery.data ?? [];

  return (
    <div
      className="border-t px-4 py-3 max-h-[240px] overflow-y-auto"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-ink">
          <Activity className="h-3.5 w-3.5" aria-hidden />
          Activity
        </div>
        <span className="text-[10px] text-ink-subtle">most recent first</span>
      </div>

      {localItems.length > 0 && (
        <ul className="space-y-1 mb-2">
          {localItems.map((entry) => (
            <li key={entry.id} className="grid grid-cols-[auto_1fr_auto] gap-2 items-center text-xs">
              <ToneDot tone={entry.tone} />
              <span className="text-ink">{entry.text}</span>
              <span className="font-mono text-[10px] text-ink-subtle">{entry.at}</span>
            </li>
          ))}
        </ul>
      )}

      {serverQuery.isError ? (
        <div className="text-[11px] text-ink-subtle">
          Couldn't load the shared activity log.{' '}
          <button
            type="button"
            className="underline hover:text-ink"
            onClick={() => void serverQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : serverItems.length === 0 && localItems.length === 0 ? (
        <div className="text-[11px] text-ink-subtle">
          No activity yet. Assignments, completions, and field captures show up here.
        </div>
      ) : (
        <ul className="space-y-1">
          {serverItems.map((event) => (
            <li
              key={event.id}
              className="grid grid-cols-[auto_1fr_auto] gap-2 items-center text-xs"
            >
              <ToneDot tone={toneForKind(event.kind)} />
              <span className="text-ink">{event.summary}</span>
              <span className="font-mono text-[10px] text-ink-subtle">
                {timeLabel(event.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function toneForKind(kind: string): DispatchActivity['tone'] {
  if (kind === 'field.complete') return 'success';
  if (kind === 'quote.promoted') return 'install';
  if (kind.startsWith('photo.') || kind.startsWith('signature.')) return 'success';
  if (kind.startsWith('parts.')) return 'warning';
  return 'info';
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ToneDot({ tone }: { tone: DispatchActivity['tone'] }) {
  const palette: Record<DispatchActivity['tone'], { bg: string; fg: string }> = {
    success: { bg: 'var(--pill-pm-bg)', fg: 'var(--pill-pm-fg)' },
    info: { bg: 'var(--pill-service-bg)', fg: 'var(--pill-service-fg)' },
    warning: { bg: 'var(--pill-sales-bg)', fg: 'var(--pill-sales-fg)' },
    danger: { bg: 'var(--pill-emergency-bg)', fg: 'var(--pill-emergency-fg)' },
    install: { bg: 'var(--pill-install-bg)', fg: 'var(--pill-install-fg)' },
  };
  const c = palette[tone];
  return (
    <span
      aria-hidden
      className="inline-block h-4 w-4 rounded"
      style={{ backgroundColor: c.bg, color: c.fg }}
    />
  );
}
